/**
 * ============================================================================
 * @altkey/i18n - Public Entry Point
 * ============================================================================
 * Punto di ingresso pubblico del package. Riesporta l'intera API di
 * `locale.ts` (store reattivo, rilevamento/persistenza lingua, MessageSchema)
 * e assembla i 5 cataloghi di traduzione in una mappa tipata, esponendo la
 * funzione `t()` per la risoluzione dinamica dei messaggi con interpolazione
 * dei placeholder.
 * ============================================================================
 */

import deCatalog from './locales/de';
import enCatalog from './locales/en';
import esCatalog from './locales/es';
import frCatalog from './locales/fr';
import itCatalog from './locales/it';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  detectBrowserLocale,
  getActiveLocale,
  getStoredLocalePreference,
  isSupportedLocaleCode,
  localeStore,
  onLocaleChange,
  persistLocalePreferenceIndexedDB,
  readLocalePreferenceIndexedDB,
  resolveInitialLocale,
  setActiveLocale,
  setStoredLocalePreference,
  type DeepMessageKeyOf,
  type LocaleCatalog,
  type LocaleCode,
  type MessageKey,
  type MessageSchema,
} from './locale';

/* ============================================================================
 * RE-EXPORT: locale.ts
 * ============================================================================
 * Tutta l'API di rilevamento, persistenza e stato reattivo della lingua
 * viene riesportata da questo barrel file, cosi' i consumer (apps/desktop,
 * apps/mobile) importano tutto da un unico punto: `@altkey/i18n`.
 * ============================================================================ */

export {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  detectBrowserLocale,
  getActiveLocale,
  getStoredLocalePreference,
  isSupportedLocaleCode,
  localeStore,
  onLocaleChange,
  persistLocalePreferenceIndexedDB,
  readLocalePreferenceIndexedDB,
  resolveInitialLocale,
  setActiveLocale,
  setStoredLocalePreference,
};

export type { DeepMessageKeyOf, LocaleCatalog, LocaleCode, MessageKey, MessageSchema };

/* ============================================================================
 * 2. MAPPA DEI CATALOGHI
 * ============================================================================
 * `Record<LocaleCode, LocaleCatalog>` con chiave OBBLIGATORIA per ciascuno dei
 * 5 LocaleCode: se in futuro venisse aggiunto un nuovo LocaleCode a
 * `locale.ts` senza il relativo catalogo qui, TypeScript segnala l'errore in
 * fase di build (proprieta' mancante), impedendo un deploy con una lingua
 * "a meta'".
 * ============================================================================ */

export const CATALOGS: Readonly<Record<LocaleCode, LocaleCatalog>> = Object.freeze({
  en: enCatalog,
  it: itCatalog,
  de: deCatalog,
  fr: frCatalog,
  es: esCatalog,
});

/**
 * Restituisce il catalogo completo per un locale specifico. Se il locale
 * richiesto non fosse presente per qualche motivo (non dovrebbe mai accadere
 * dato il tipo `Record` esaustivo sopra), ricade su `DEFAULT_LOCALE`.
 */
export function getCatalog(locale: LocaleCode): LocaleCatalog {
  return CATALOGS[locale] ?? CATALOGS[DEFAULT_LOCALE];
}

/* ============================================================================
 * 3. RISOLUZIONE MESSAGGI + INTERPOLAZIONE PLACEHOLDER
 * ============================================================================ */

/**
 * Valori accettati per l'interpolazione di un placeholder `{nome}`. Numeri e
 * stringhe sono gli unici tipi sensati per un messaggio testuale; qualunque
 * altro tipo andrebbe convertito esplicitamente dal chiamante prima di
 * passarlo a `t()`.
 */
export type MessageParams = Readonly<Record<string, string | number>>;

const PLACEHOLDER_PATTERN = /\{(\w+)\}/g;

/**
 * Naviga `MessageSchema` seguendo un percorso "dot-path" (es.
 * "pairing.title") e restituisce la stringa foglia corrispondente. Lancia un
 * errore esplicito e descrittivo se il percorso non risolve a una stringa:
 * questo non dovrebbe MAI accadere a runtime dato che `MessageKey` e'
 * generato staticamente da `MessageSchema`, ma la guardia resta utile per
 * chiamate con chiavi costruite dinamicamente (es. da CMS esterno) che
 * bypassano il type-check.
 */
function resolveMessagePath(messages: MessageSchema, path: string): string {
  const segments = path.split('.');
  let current: unknown = messages;

  for (const segment of segments) {
    if (typeof current !== 'object' || current === null || !(segment in current)) {
      throw new Error(
        `[@altkey/i18n] Chiave di traduzione non trovata: "${path}" (segmento mancante: "${segment}")`,
      );
    }
    current = (current as Record<string, unknown>)[segment];
  }

  if (typeof current !== 'string') {
    throw new Error(
      `[@altkey/i18n] La chiave "${path}" non risolve a una stringa (namespace incompleto?)`,
    );
  }

  return current;
}

/**
 * Sostituisce ogni placeholder `{nome}` presente nel messaggio con il valore
 * corrispondente in `params`. Comportamento robusto e prevedibile:
 *  - Se `params` non e' fornito, il messaggio viene restituito invariato.
 *  - Se un placeholder nel testo non ha un parametro corrispondente, viene
 *    lasciato invariato (non sostituito con stringa vuota) e viene loggato
 *    un avviso.
 *  - I parametri numerici vengono convertiti a stringa con `String()`.
 */
function interpolate(message: string, params?: MessageParams): string {
  if (!params) return message;

  return message.replace(PLACEHOLDER_PATTERN, (fullMatch, paramName: string) => {
    if (!(paramName in params)) {
      console.warn(
        `[@altkey/i18n] Placeholder "{${paramName}}" senza parametro corrispondente nel messaggio: "${message}"`,
      );
      return fullMatch;
    }
    return String(params[paramName]);
  });
}

/**
 * Risolve e interpola un messaggio tradotto per la lingua ATTIVA (quella
 * corrente in `localeStore`).
 *
 * @param key    Chiave "dot-path" tipata, es. "pairing.title" o "search.resultsCount".
 * @param params Parametri opzionali per l'interpolazione dei placeholder `{nome}`.
 *
 * @example
 * t('common.appName') // "AltKey"
 * t('search.resultsCount', { count: 42 }) // "42 risultati trovati" (locale attivo: it)
 */
export function t(key: MessageKey, params?: MessageParams): string {
  const activeLocale = getActiveLocale();
  const catalog = getCatalog(activeLocale);
  const rawMessage = resolveMessagePath(catalog.messages, key);
  return interpolate(rawMessage, params);
}

/**
 * Variante esplicita di `t()` che risolve il messaggio per un locale
 * SPECIFICO, indipendentemente dalla lingua attiva nello store. Utile per
 * contesti che non devono dipendere dallo stato globale (es. generazione di
 * notifiche push o email lato `apps/backend`, dove la lingua e' quella
 * salvata nel profilo utente, non quella del browser che sta eseguendo il
 * codice).
 *
 * @example
 * tFor('de', 'pairing.connected') // "Gerät verbunden"
 */
export function tFor(locale: LocaleCode, key: MessageKey, params?: MessageParams): string {
  const catalog = getCatalog(locale);
  const rawMessage = resolveMessagePath(catalog.messages, key);
  return interpolate(rawMessage, params);
}